import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, Loader2, X, ChevronUp, ChevronDown, Eye, EyeOff, AlertCircle, CheckCircle, Image as ImageIcon, CalendarClock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import BannerSlide from './BannerSlide';
import ImageUploadField from './ImageUploadField';
import { BANNER_COLORS } from '../data/bannerColors';
import { bannersAPI } from '../services/api';
import TranslationsEditor from './TranslationsEditor';

const EMPTY = { title: '', subtitle: '', description: '', color: 'orange', image: '', link: '', button_label: '', active: true, starts_at: '', ends_at: '', i18n: {} };
const TR_FIELDS = [['title', 'Titre'], ['subtitle', 'Sous-titre'], ['description', 'Description'], ['button_label', 'Texte du bouton']];
const STATUS = { live: ['Visible', 'bg-green-100 text-green-700'], scheduled: ['Programmée', 'bg-sky-100 text-sky-700'], expired: ['Expirée', 'bg-red-100 text-red-700'], inactive: ['Inactive', 'bg-gray-100 text-gray-600'] };
const fmtDt = (v) => (v ? new Date(v).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '');

const AdminBannersPanel = () => {
  const [banners, setBanners] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | banner
  const [form, setForm] = useState(EMPTY);
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { setBanners(await bannersAPI.getAll()); } finally { setIsLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setMessage(null); setEditing('new'); };
  const openEdit = (b) => { setForm({ ...EMPTY, ...b, starts_at: b.starts_at || '', ends_at: b.ends_at || '', i18n: { ...(b.i18n || {}) } }); setMessage(null); setEditing(b); };
  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const save = async () => {
    setMessage(null);
    if (!form.title.trim()) { setMessage({ type: 'error', text: 'Le titre est obligatoire' }); return; }
    setSaving(true);
    try {
      const { id, position, schedule_status, ...payload } = form;
      if (editing === 'new') await bannersAPI.create(payload);
      else await bannersAPI.update(editing.id, payload);
      await load();
      setEditing(null);
      setMessage({ type: 'success', text: editing === 'new' ? 'Bannière créée' : 'Bannière mise à jour' });
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Erreur lors de l\'enregistrement' });
    } finally { setSaving(false); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Supprimer la bannière « ${b.title} » ?`)) return;
    await bannersAPI.delete(b.id);
    await load();
    setMessage({ type: 'success', text: 'Bannière supprimée' });
  };

  const toggle = async (b) => {
    const { id, position, schedule_status, ...payload } = b;
    await bannersAPI.update(b.id, { ...payload, active: !b.active });
    await load();
  };

  const move = async (idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= banners.length) return;
    const next = [...banners];
    [next[idx], next[to]] = [next[to], next[idx]];
    setBanners(next);
    await bannersAPI.reorder(next.map(b => b.id));
  };

  const activeCount = banners.filter(b => b.schedule_status === 'live').length;
  const scheduledCount = banners.filter(b => b.schedule_status === 'scheduled').length;

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bannières d'accueil ({banners.length})</h1>
          <p className="text-sm text-gray-500 mt-1">{activeCount} visible(s) dans le carrousel de la page d'accueil{scheduledCount ? `, ${scheduledCount} programmée(s)` : ''} — défilement automatique toutes les 5 s. Programmez une période d'affichage pour que la bannière apparaisse et disparaisse automatiquement.</p>
        </div>
        <Button data-testid="admin-add-banner-button" onClick={openNew} disabled={banners.length >= 10} className="bg-orange-500 hover:bg-orange-600">
          <Plus size={18} className="mr-2" /> Nouvelle bannière
        </Button>
      </div>

      {message && !editing && (
        <p data-testid="banners-message" className={`mb-4 text-sm flex items-center gap-2 ${message.type === 'error' ? 'text-red-600' : 'text-green-700'}`}>
          {message.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle size={16} />} {message.text}
        </p>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-orange-500" /></div>
      ) : banners.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center text-gray-500" data-testid="banners-empty">Aucune bannière. Le carrousel est masqué sur l'accueil.</div>
      ) : (
        <div className="space-y-3" data-testid="banners-list">
          {banners.map((b, idx) => (
            <div key={b.id} data-testid={`banner-row-${idx}`} className={`bg-white rounded-lg shadow-sm p-3 flex flex-wrap items-center gap-4 ${b.active ? '' : 'opacity-60'}`}>
              <div className="flex flex-col gap-1">
                <button data-testid={`banner-up-${idx}`} onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1 text-gray-500 hover:text-orange-600 disabled:opacity-30"><ChevronUp size={16} /></button>
                <button data-testid={`banner-down-${idx}`} onClick={() => move(idx, 1)} disabled={idx === banners.length - 1} className="p-1 text-gray-500 hover:text-orange-600 disabled:opacity-30"><ChevronDown size={16} /></button>
              </div>
              <div className="w-full sm:w-72 rounded-md overflow-hidden flex-shrink-0">
                <BannerSlide banner={b} compact />
              </div>
              <div className="flex-1 min-w-[12rem]">
                <p className="font-semibold text-gray-900 truncate" data-testid={`banner-title-${idx}`}>{b.title}</p>
                <p className="text-xs text-gray-500 truncate">{[b.subtitle, b.description].filter(Boolean).join(' · ')}</p>
                <p className="text-xs text-gray-400 truncate mt-1">
                  {b.link ? <>Lien : <span className="text-blue-600">{b.link}</span>{b.button_label && ` (« ${b.button_label} »)`}</> : 'Sans lien'}
                  {b.image && <span className="ml-2 inline-flex items-center gap-1"><ImageIcon size={11} /> image</span>}
                </p>
                {(b.starts_at || b.ends_at) && <p className="text-xs text-sky-700 mt-0.5 inline-flex items-center gap-1" data-testid={`banner-window-${idx}`}><CalendarClock size={11} /> {b.starts_at ? `du ${fmtDt(b.starts_at)}` : ''}{b.ends_at ? ` au ${fmtDt(b.ends_at)}` : ' (sans fin)'}</p>}
              </div>
              <span data-testid={`banner-status-${idx}`} className={`text-xs px-2 py-1 rounded-full ${(STATUS[b.schedule_status] || STATUS.inactive)[1]}`}>{(STATUS[b.schedule_status] || STATUS.inactive)[0]}</span>
              <span data-testid={`banner-i18n-${idx}`} className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${Object.values(b.i18n || {}).filter(v => v?.title).length === 4 ? 'bg-sky-100 text-sky-700' : 'bg-gray-100 text-gray-500'}`}>{Object.values(b.i18n || {}).filter(v => v?.title).length + 1}/5 langues</span>
              <div className="flex gap-1">
                <button data-testid={`banner-toggle-${idx}`} onClick={() => toggle(b)} title={b.active ? 'Désactiver' : 'Activer'} className="p-2 text-gray-600 hover:bg-gray-100 rounded">{b.active ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                <button data-testid={`banner-edit-${idx}`} onClick={() => openEdit(b)} className="p-2 text-blue-600 hover:bg-blue-50 rounded"><Edit size={16} /></button>
                <button data-testid={`banner-delete-${idx}`} onClick={() => remove(b)} className="p-2 text-red-600 hover:bg-red-50 rounded"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="banner-form">
            <div className="p-5 border-b bg-gradient-to-r from-orange-500 to-orange-600 text-white flex items-center justify-between">
              <h2 className="text-lg font-bold" data-testid="banner-form-title">{editing === 'new' ? 'Nouvelle bannière' : `Modifier « ${editing.title} »`}</h2>
              <button onClick={() => setEditing(null)} className="text-white/80 hover:text-white"><X size={22} /></button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto">
              {message && <p data-testid="banner-form-error" className="text-sm text-red-600 flex items-center gap-2"><AlertCircle size={16} /> {message.text}</p>}
              <div className="rounded-lg overflow-hidden border" data-testid="banner-form-preview">
                <BannerSlide banner={{ ...form, title: form.title || 'Titre de la bannière' }} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Titre *</label>
                  <Input data-testid="banner-title-input" value={form.title} onChange={set('title')} placeholder="SUPER OFFRE" maxLength={60} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Sous-titre</label>
                  <Input data-testid="banner-subtitle-input" value={form.subtitle} onChange={set('subtitle')} placeholder="Livraison gratuite" maxLength={80} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                  <Input data-testid="banner-description-input" value={form.description} onChange={set('description')} placeholder="à partir de 120 €" maxLength={120} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Lien (interne /categorie/frein ou https://…)</label>
                  <Input data-testid="banner-link-input" value={form.link} onChange={set('link')} placeholder="/pieces" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Texte du bouton</label>
                  <Input data-testid="banner-button-input" value={form.button_label} onChange={set('button_label')} placeholder="Découvrir" maxLength={30} />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-2">Couleur de fond</label>
                  <div className="flex flex-wrap gap-2" data-testid="banner-color-palette">
                    {BANNER_COLORS.map(c => (
                      <button key={c.value} type="button" data-testid={`banner-color-${c.value}`} onClick={() => setForm(f => ({ ...f, color: c.value }))} title={c.label}
                              className={`w-9 h-9 rounded-full ${c.swatch} border-4 transition-transform hover:scale-105 ${form.color === c.value ? 'border-gray-900' : 'border-white shadow'}`} />
                    ))}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <ImageUploadField label="Image de fond (optionnelle, voile sombre appliqué)" testId="banner-image" value={form.image} onChange={(url) => setForm(f => ({ ...f, image: url }))} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Afficher à partir du (optionnel)</label>
                  <Input type="datetime-local" data-testid="banner-starts-input" value={form.starts_at} onChange={set('starts_at')} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Masquer après le (optionnel)</label>
                  <Input type="datetime-local" data-testid="banner-ends-input" value={form.ends_at} onChange={set('ends_at')} />
                  {form.starts_at && form.ends_at && form.ends_at <= form.starts_at && <p className="text-xs text-red-600 mt-1" data-testid="banner-window-error">La fin doit être après le début.</p>}
                </div>
                <div className="md:col-span-2">
                  <TranslationsEditor testId="banner-translations" title="Textes de la bannière dans les autres langues" context="home-page promotional banner"
                    fields={TR_FIELDS.filter(([k]) => (form[k] || '').trim()).map(([k, label]) => ({ key: k, label, source: form[k] }))}
                    values={form.i18n || {}} onChange={(i18n) => setForm(f => ({ ...f, i18n }))} />
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer md:col-span-2">
                  <input type="checkbox" data-testid="banner-active-checkbox" checked={form.active} onChange={(e) => setForm(f => ({ ...f, active: e.target.checked }))} className="rounded border-gray-300 text-orange-500" />
                  Bannière active (visible sur l'accueil{form.starts_at || form.ends_at ? ' pendant la période programmée' : ''})
                </label>
              </div>
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setEditing(null)} data-testid="banner-form-cancel">Annuler</Button>
              <Button data-testid="banner-form-submit" onClick={save} disabled={saving} className="bg-green-600 hover:bg-green-700">
                {saving && <Loader2 size={16} className="mr-2 animate-spin" />} {editing === 'new' ? 'Créer' : 'Enregistrer'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminBannersPanel;
